<template>
    <draggable
      v-if="draggable"
      v-model="localItems"
      tag="div"
      class="widget-masonry"
      :class="{ 'is-resizing': resizingKey !== null }"
      :style="gridStyle"
      :disabled="disabled"
      :handle="handle"
      @mousedown.native="onResizeStart"
      @end="onDragEnd"
    >
    <div
      v-for="(item, index) in localItems"
      :key="getItemKey(item, index)"
      class="masonry-item"
      :class="getItemClass(item, index)"
      :data-key="getItemKey(item, index)"
      :style="getItemStyle(item, index)"
      ref="items"
    >
      <div class="masonry-content" :data-key="getItemKey(item, index)" :data-fixed-height="hasFixedHeight(item) ? '1' : null" :style="getContentStyle(item, index)" ref="contents">
        <slot :item="item"></slot>
      </div>
    </div>
  </draggable>
  <div v-else class="widget-masonry" :class="{ 'is-resizing': resizingKey !== null }" :style="gridStyle" @mousedown="onResizeStart">
    <div
      v-for="(item, index) in localItems"
      :key="getItemKey(item, index)"
      class="masonry-item"
      :class="getItemClass(item, index)"
      :data-key="getItemKey(item, index)"
      :style="getItemStyle(item, index)"
      ref="items"
    >
      <div class="masonry-content" :data-key="getItemKey(item, index)" :data-fixed-height="hasFixedHeight(item) ? '1' : null" :style="getContentStyle(item, index)" ref="contents">
        <slot :item="item"></slot>
      </div>
    </div>
  </div>
</template>

<script>
  import draggable from 'vuedraggable'

  export default {
    model: {
      prop: 'items',
      event: 'input'
    },
    components: {
      draggable
    },
    props: {
      items: {
        type: Array,
        default: () => []
      },
      disabled: {
        type: Boolean,
        default: false
      },
      draggable: {
        type: Boolean,
        default: true
      },
      handle: {
        type: String,
        default: '.widget-drag-handle'
      },
      minHeight: {
        type: Number,
        default: 140
      },
      rowHeight: {
        type: Number,
        default: 8
      },
      gap: {
        type: Number,
        default: 12
      },
      columns: {
        type: Number,
        default: 12
      },
      keyProp: {
        type: String,
        default: 'reference'
      },
      colsProp: {
        type: String,
        default: 'cols'
      }
    },
    data: () => {
      return {
        localItems: [],
        itemSpans: {},
        resizeObserver: null,
        measureTimer: null,
        measureTicks: 0,
        resizeState: null,
        resizingKey: null,
        isMobile: false
      }
    },
    computed: {
      gridStyle() {
        const cols = this.isMobile ? 1 : this.columns
        return {
          gridTemplateColumns: 'repeat(' + cols + ', 1fr)',
          gridAutoRows: this.rowHeight + 'px',
          gridGap: this.gap + 'px'
        }
      }
    },
      watch: {
      items: {
        handler(value) {
          const nextItems = Array.isArray(value) ? value.slice() : []
          const prevKeys = this.localItems.map((item, index) => this.getItemKey(item, index))
          const nextKeys = nextItems.map((item, index) => this.getItemKey(item, index))
          const sameKeys = prevKeys.length === nextKeys.length && prevKeys.every((key, index) => key === nextKeys[index])

          this.localItems = nextItems
          if(!sameKeys) {
            this.itemSpans = {}
          }
          this.$nextTick(() => {
            this.rebuildObservers()
            this.measureAll()
            this.scheduleMeasureBurst()
          })
        },
        immediate: true
      }
    },
    mounted() {
      this.updateMobileState()
      if(typeof ResizeObserver !== 'undefined') {
        this.resizeObserver = new ResizeObserver((entries) => {
          entries.forEach((entry) => {
            this.updateItemSpan(entry.target)
          })
        })
      }

      window.addEventListener('resize', this.onWindowResize)
      this.$nextTick(() => {
        this.rebuildObservers()
        this.measureAll()
        this.scheduleMeasureBurst()
      })
    },
    beforeDestroy() {
      window.removeEventListener('resize', this.onWindowResize)
      this.disconnectObservers()
      this.clearMeasureBurst()
      this.teardownResizeListeners()
    },
    methods: {
      onWindowResize() {
        this.updateMobileState()
        this.measureAll()
      },
      updateMobileState() {
        this.isMobile = window.innerWidth <= 992
      },
      onDragEnd() {
        this.$emit('input', this.localItems)
        this.$emit('order-end', this.localItems)
        this.$nextTick(this.measureAll)
      },
      onResizeStart(event) {
        if(this.disabled) {
          return
        }
        const handleX = event.target.closest('.widget-resize-handle-x')
        const handleY = event.target.closest('.widget-resize-handle-y')
        if(!handleX && !handleY) {
          return
        }
        const handle = handleX || handleY
        const itemEl = handle.closest('.masonry-item')
        if(!itemEl) {
          return
        }

        event.preventDefault()
        event.stopPropagation()

        const key = itemEl.dataset.key
        const item = this.localItems.find((entry, index) => {
          return this.getItemKey(entry, index) == key
        })
        if(!item) {
          return
        }

        const containerRect = this.$el.getBoundingClientRect()
        const itemRect = itemEl.getBoundingClientRect()
        const colWidth = (containerRect.width - this.gap * (this.columns - 1)) / this.columns
        const startCols = this.getItemCols(item)
        const startHeight = itemRect.height

        this.resizeState = {
          key: key,
          mode: handleY ? 'y' : 'x',
          startX: event.clientX,
          startY: event.clientY,
          startWidth: itemRect.width,
          startHeight: startHeight,
          colWidth: colWidth,
          startCols: startCols,
          cols: startCols,
          height: startHeight
        }
        this.resizingKey = key

        this.$emit('resize-start', { reference: key, cols: startCols, height: startHeight })
        this.setupResizeListeners()
      },
      onResizeMove(event) {
        if(!this.resizeState) {
          return
        }
        if(this.resizeState.mode === 'x') {
          const deltaX = event.clientX - this.resizeState.startX
          const desiredWidth = Math.max(this.resizeState.colWidth, this.resizeState.startWidth + deltaX)
          const colsFloat = (desiredWidth + this.gap) / (this.resizeState.colWidth + this.gap)
          const cols = Math.max(1, Math.min(this.columns, Math.round(colsFloat)))

          if(cols !== this.resizeState.cols) {
            this.resizeState.cols = cols
            this.updateLocalItemSize(this.resizeState.key, { cols: cols })
            this.$emit('resize-change', { reference: this.resizeState.key, cols: cols })
            this.$nextTick(this.measureAll)
          }
        } else {
          const deltaY = event.clientY - this.resizeState.startY
          const desiredHeight = Math.max(this.minHeight, this.resizeState.startHeight + deltaY)
          const height = Math.round(desiredHeight)
          if(height !== this.resizeState.height) {
            this.resizeState.height = height
            this.updateLocalItemSize(this.resizeState.key, { height: height })
            this.$emit('resize-change', { reference: this.resizeState.key, height: height })
            this.$nextTick(this.measureAll)
          }
        }
      },
      onResizeEnd() {
        if(!this.resizeState) {
          return
        }
        var payload = { reference: this.resizeState.key }
        if (this.resizeState.mode === 'x') {
          payload.cols = this.resizeState.cols
        } else {
          payload.height = this.resizeState.height
        }
        this.$emit('resize-end', payload)
        this.resizeState = null
        this.resizingKey = null
        this.teardownResizeListeners()
        this.$nextTick(this.measureAll)
      },
      setupResizeListeners() {
        window.addEventListener('mousemove', this.onResizeMove)
        window.addEventListener('mouseup', this.onResizeEnd)
      },
      teardownResizeListeners() {
        window.removeEventListener('mousemove', this.onResizeMove)
        window.removeEventListener('mouseup', this.onResizeEnd)
      },
      getItemKey(item, index) {
        if(item && item[this.keyProp] !== undefined && item[this.keyProp] !== null) {
          return item[this.keyProp]
        }
        return index
      },
      getItemCols(item) {
        if(this.isMobile) {
          return 1
        }
        var cols = parseInt(item && item[this.colsProp], 10)
        if(!cols || cols < 1 || cols > this.columns) {
          cols = 4
        }
        return cols
      },
      getItemStyle(item, index) {
        const key = this.getItemKey(item, index)
        const span = this.itemSpans[key] || 1
        return {
          gridColumnEnd: 'span ' + this.getItemCols(item),
          gridRowEnd: 'span ' + span
        }
      },
      getItemClass(item, index) {
        const key = this.getItemKey(item, index)
        return {
          'is-resizing': this.resizingKey !== null && this.resizingKey == key
        }
      },
      updateLocalItemSize(key, payload) {
        const index = this.localItems.findIndex((entry, idx) => this.getItemKey(entry, idx) == key)
        if(index === -1) {
          return
        }
        const current = this.localItems[index] || {}
        const next = Object.assign({}, current)
        if(payload.cols !== undefined) {
          next[this.colsProp] = payload.cols
        }
        if(payload.height !== undefined) {
          next.height = payload.height
        }
        this.$set(this.localItems, index, next)
      },
      hasFixedHeight(item) {
        // En mobile las cards se pliegan y su altura debe surgir del contenido
        // visible, no de la medida configurada para el tablero de escritorio.
        if(this.isMobile) {
          return false
        }
        const height = item && item.height ? parseInt(item.height, 10) : null
        return !!(height && height > 0)
      },
      getContentStyle(item) {
        if(this.isMobile) {
          return {}
        }
        const height = item && item.height ? parseInt(item.height, 10) : null
        if(height && height > 0) {
          return {
            height: height + 'px'
          }
        }
        return {}
      },
      rebuildObservers() {
        this.disconnectObservers()
        if(!this.resizeObserver) {
          return
        }
        const elements = this.$refs.contents ? this.$refs.contents : []
        const list = Array.isArray(elements) ? elements : [elements]
        list.forEach((el) => {
          if(el) {
            this.resizeObserver.observe(el)
          }
        })
      },
      disconnectObservers() {
        if(!this.resizeObserver) {
          return
        }
        const elements = this.$refs.contents ? this.$refs.contents : []
        const list = Array.isArray(elements) ? elements : [elements]
        list.forEach((el) => {
          if(el) {
            this.resizeObserver.unobserve(el)
          }
        })
      },
      updateItemSpan(el) {
        if(!el) {
          return
        }
        const rectHeight = el.getBoundingClientRect().height
        const scrollHeight = el.scrollHeight
        const fixedHeight = el.dataset && el.dataset.fixedHeight === '1'
        const height = fixedHeight ? rectHeight : Math.max(rectHeight, scrollHeight)
        const span = Math.max(1, Math.ceil((height + this.gap) / (this.rowHeight + this.gap)))
        const key = el.dataset.key
        if(key !== undefined) {
          this.$set(this.itemSpans, key, span)
        }
      },
      measureAll() {
        this.$nextTick(() => {
          const elements = this.$refs.contents ? this.$refs.contents : []
          const list = Array.isArray(elements) ? elements : [elements]
          list.forEach((el) => {
            this.updateItemSpan(el)
          })
        })
      },
      scheduleMeasureBurst() {
        this.clearMeasureBurst()
        this.measureTicks = 0
        this.measureTimer = setInterval(() => {
          this.measureTicks += 1
          this.measureAll()
          if(this.measureTicks >= 12) {
            this.clearMeasureBurst()
          }
        }, 300)
      },
      clearMeasureBurst() {
        if(this.measureTimer) {
          clearInterval(this.measureTimer)
          this.measureTimer = null
        }
      }
    }
  }
</script>

<style>
  .widget-masonry {
    display: grid;
    grid-auto-flow: dense;
    align-items: start;
  }
  @media (max-width: 992px) {
    .widget-masonry {
      grid-template-columns: repeat(1, minmax(0, 1fr)) !important;
      grid-auto-flow: row;
    }
    .widget-masonry .masonry-item {
      grid-column: 1 / -1 !important;
      width: 100%;
    }
    .widget-masonry .masonry-content {
      width: 100%;
    }
  }
  .masonry-content[data-fixed-height="1"] .widget-list-custom {
    max-height: 100%;
  }
  .masonry-item {
    margin: 0;
    position: relative;
  }
  .masonry-content {
    display: flow-root;
  }
  .masonry-content[data-fixed-height="1"] {
    overflow: hidden;
  }
  .masonry-content[data-fixed-height="1"] > * {
    height: 100%;
  }
  .masonry-content[data-fixed-height="1"] .widget-shell {
    height: 100%;
  }
  .masonry-content[data-fixed-height="1"] .widget-custom {
    height: 100%;
  }
  .masonry-content[data-fixed-height="1"] .widget-content-wrapper {
    overflow: hidden;
  }
  .masonry-content[data-fixed-height="1"] .widget-body {
    overflow: auto;
  }
  .masonry-item.is-resizing {
    z-index: 5;
  }
  .masonry-item.is-resizing .widget-card {
    border-color: var(--app-color-primary);
    box-shadow: var(--app-shadow-md);
  }
  .widget-masonry.is-resizing iframe {
    pointer-events: none;
  }
</style>
